import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Loading from '@karehero/llama/components/pages/Loading/Loading';
import { ConversationalFormPage } from '@karehero/llama/pwa/pages/ConversationalFormPage/ConversationalFormPage';
import { ChatCard } from '@karehero/llama/pwa/pages/ConversationalFormPage/ChatCard';
import { ReadingLine } from '@karehero/llama/pwa/pages/ConversationalFormPage/ReadingLine';
import type { RecorderProps } from '@karehero/llama/pwa/pages/ConversationalFormPage/ChatComposer';
import type { UploadSource } from '@karehero/llama/pwa/pages/ConversationalFormPage/UploadSheet';
import { FactReviewPage } from '@karehero/llama/pwa/pages/FactReviewPage/FactReviewPage';
import { OnboardingPageTemplate } from '@karehero/llama/pwa/templates/OnboardingPageTemplate/OnboardingPageTemplate';
import {
  ACCEPTED_DOCUMENT_TYPES_INPUT,
  ACCEPTED_IMAGE_TYPES,
} from '@karehero/helpers/files';
import { joinLines } from '@karehero/helpers/string';

import { ROUTES } from 'router/routes';
import { useSelector } from 'store';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { getAccount } from 'store/slices/iamSlice';
import { MIC_DENIED_ERROR } from 'hooks/useAudioRecorder';
import { useAuth } from 'hooks/useAuth';
import { useDocumentUrl } from 'hooks/useDocumentUrl';
import { useEventStream } from 'hooks/useEventStream';
import useIntercom from 'hooks/useIntercom';
import { useOnboardingConsentStyle } from 'hooks/useOnboardingConsentStyle';
import { useOnboardingRoute } from 'hooks/useOnboardingRoute';
import { useOnboardingV4 } from 'hooks/useOnboardingV4';
import { useOpenUrl } from 'hooks/useOpenUrl';
import { useVoiceNoteDraft } from 'hooks/useVoiceNoteDraft';
import {
  DEFAULT_ONBOARDING_V4_FLOW_ID,
  isOnboardingV4FlowID,
} from 'config/onboardingV4/flows';
import { START_BOOK_CALL } from 'config/onboardingV4/beatSteps';
import { CONFIRM, READING } from 'config/onboardingV4/copy';

const NO_PHOTOS: File[] = [];

/** The flow a new session starts, and where it asks consent: the flags', unknown until they load. */
const useNewSession = () => {
  const route = useOnboardingRoute();
  const consent = useOnboardingConsentStyle();
  if (!route.isReady || !consent.isReady) return {};
  return {
    flowID: isOnboardingV4FlowID(route.variant)
      ? route.variant
      : DEFAULT_ONBOARDING_V4_FLOW_ID,
    consentStyle: consent.variant,
  };
};

/** OnboardingMemberCreateV4 wires onboarding v4: one conversation, then the details to check. */
export const OnboardingMemberCreateV4 = () => {
  const navigate = useNavigate();
  const onFinished = useCallback(
    (start?: string) =>
      navigate(start === START_BOOK_CALL ? ROUTES.BOOK_CALL : ROUTES.HOME),
    [navigate],
  );
  const onDeclined = useCallback(
    () => navigate(ROUTES.ONBOARDING, { replace: true }),
    [navigate],
  );

  const careCircle = useSelector(getCurrentCareCircle);
  const account = useSelector(getAccount);
  const voiceNote = useVoiceNoteDraft(careCircle?.id);
  const [typedText, setTypedText] = useState('');
  const isComposing =
    voiceNote.recorderProps.state === 'recording' || !!typedText.trim();
  const liveText = joinLines([voiceNote.liveText, typedText]);
  const isMicBlocked = voiceNote.recorderProps.error === MIC_DENIED_ERROR;

  const onboarding = useOnboardingV4({
    ...useNewSession(),
    isComposing,
    onFinished,
    onDeclined,
    liveText,
    isMicBlocked,
  });
  const { flow, currentStep } = onboarding;

  const getDocumentUrl = useDocumentUrl(careCircle?.id);
  const { openExternalUrl } = useOpenUrl();
  const { signOut, getToken } = useAuth();
  const { showIntercom } = useIntercom();
  const { startEventStream } = useEventStream();

  // Outside Root, which opens the stream elsewhere; the reading waits on its
  // events. Keyed on the id, as a saved name changes the account object.
  const accountID = account?.id;
  useEffect(() => {
    if (!accountID) return;
    return startEventStream({ getToken });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountID]);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The card whose lines are being corrected, if any.
  const [editingCardID, setEditingCardID] = useState<string>();
  const editingCard = onboarding.messages.find((m) => m.id === editingCardID);
  const editingLines =
    editingCard?.content.kind === 'card' ? editingCard.content.lines : [];

  // Held for the step they were picked on, like the composer's typed text.
  const [held, setHeld] = useState<{ stepID?: string; photos: File[] }>({
    photos: NO_PHOTOS,
  });
  const heldPhotos = held.stepID === currentStep?.id ? held.photos : NO_PHOTOS;
  const setHeldPhotos = useCallback(
    (photos: File[]) => setHeld({ stepID: currentStep?.id, photos }),
    [currentStep?.id],
  );

  const handleAttachFile = (source: UploadSource) => {
    const input = source === 'photos' ? photoInputRef : fileInputRef;
    input.current?.click();
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    onboarding.attachFiles(Array.from(input.files ?? []));
    input.value = '';
  };

  // On an open question photos wait in the composer; elsewhere they send at once.
  const handlePhotosSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const photos = Array.from(input.files ?? []);
    input.value = '';
    if (currentStep?.mode === 'expand') {
      setHeldPhotos([...heldPhotos, ...photos]);
    } else {
      onboarding.attachFiles(photos);
    }
  };

  const handleSend = (text: string, photos: File[]) =>
    photos.length
      ? onboarding.sendWithPhotos(text, photos)
      : onboarding.sendText(text);

  const handleOpenDocument = async (documentId: string) => {
    const url = await getDocumentUrl(documentId);
    if (url) openExternalUrl(url);
  };

  // While they record or type, the chat and its bar dim around a held checklist.
  const isHeldFocused = !!onboarding.heldCardID && isComposing;

  const withAppBar = (section: string | undefined, page: React.ReactNode) => (
    <OnboardingPageTemplate
      section={section}
      email={account?.email}
      onSignOut={signOut}
      onHelp={showIntercom}
      isAppBarDimmed={isHeldFocused}
    >
      {page}
    </OnboardingPageTemplate>
  );

  if (
    !onboarding.isSessionReady ||
    onboarding.stage === 'finished' ||
    onboarding.isFinishingInChat
  ) {
    return <Loading />;
  }

  if (onboarding.stage === 'confirm') {
    const [about] = onboarding.confirmCategories;
    return withAppBar(
      CONFIRM.section,
      <FactReviewPage
        title={about.label}
        description={CONFIRM.description}
        confirmLabel={CONFIRM.confirm}
        isLoading={onboarding.isFinishing}
        categories={onboarding.confirmCategories}
        onConfirm={onboarding.confirm}
        onSaveFact={onboarding.saveFact}
      />,
    );
  }

  const recorderProps: RecorderProps = {
    ...voiceNote.recorderProps,
    // Keni says how to let the microphone in, in place of the bar's note.
    error: isMicBlocked ? null : voiceNote.recorderProps.error,
    onSend: () => voiceNote.send(onboarding.sendVoiceNote),
  };

  // What is happening while they wait: lines said in turn, or the work shown
  // as a checklist as it is done.
  const isChecklist = onboarding.readingStyle === 'checklist';
  const threadEnd = onboarding.isReading ? (
    <>
      {isChecklist && (
        <ChatCard title={READING.cardTitle} items={onboarding.readingItems} />
      )}
      <ReadingLine lines={onboarding.readingLines ?? READING.lines} />
    </>
  ) : onboarding.isWritingUp ? (
    <ReadingLine
      lines={
        isChecklist
          ? READING.writingUpLines
          : (onboarding.readingLines ?? READING.lines)
      }
    />
  ) : undefined;

  return withAppBar(
    onboarding.section,
    <>
      <ConversationalFormPage
        messages={onboarding.messages}
        currentStep={currentStep}
        isThinking={onboarding.isThinking}
        onChoose={onboarding.answerWithOption}
        onSend={handleSend}
        photos={heldPhotos}
        setPhotos={setHeldPhotos}
        recorder={recorderProps}
        onAttachFile={handleAttachFile}
        onOpenLink={openExternalUrl}
        onOpenDocument={handleOpenDocument}
        getDocumentUrl={getDocumentUrl}
        onResend={onboarding.resendMessage}
        voiceControl={flow.voiceControl}
        choiceStyle={onboarding.choiceStyle}
        threadEnd={threadEnd}
        onEditCard={setEditingCardID}
        onPinCard={onboarding.togglePinned}
        onDraftChange={setTypedText}
        onDraftBlur={onboarding.onDraftBlur}
        replyTone='gardenia'
        openMenuId={onboarding.openMenuID}
        isVoiceTextShown={recorderProps.isCaptionShown}
        onToggleVoiceText={recorderProps.onToggleCaption}
        heldCardId={onboarding.heldCardID}
        isPinnedFocused={isHeldFocused}
        editingCard={
          editingCard?.content.kind === 'card' && editingCardID
            ? {
                id: editingCardID,
                title: editingCard.content.title,
                lines: editingLines,
                onSave: (lines) => {
                  onboarding.editSummary(editingCardID, lines);
                  setEditingCardID(undefined);
                },
                onCancel: () => setEditingCardID(undefined),
              }
            : undefined
        }
      />
      <input
        ref={photoInputRef}
        type='file'
        hidden
        multiple
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        onChange={handlePhotosSelected}
      />
      <input
        ref={fileInputRef}
        type='file'
        hidden
        multiple
        accept={ACCEPTED_DOCUMENT_TYPES_INPUT}
        onChange={handleFilesSelected}
      />
    </>,
  );
};

OnboardingMemberCreateV4.displayName = 'OnboardingMemberCreateV4';
export default OnboardingMemberCreateV4;
